'use client';

import { useAuth } from '@/hooks/useAuth';
import { db } from '@/firebase/config';
import { collection, query, orderBy, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { Carta, PapeletaDeSitio, UserProfile } from '@/lib/types';
import { sanitizeFirestoreData } from '@/lib/utils';
import { Loader2, Mail, MailOpen, Inbox, FileText, Clock, Building2, Vote } from 'lucide-react';
import { VotacionCarta } from '@/components/hermandades/VotacionCarta';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import { PapeletaDeSitioDoc } from '@/components/hermandades/PapeletaDeSitio';
import { getCartas } from '@/lib/actions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';

function toDateSafe(v: any): Date {
    if (!v) return new Date(0);
    if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? new Date(0) : d;
}

export default function JartinetBuzonPage() {
    const { user, loading: authLoading, userData } = useAuth();
    const [cartas, setCartas] = useState<Carta[]>([]);
    const [papeletas, setPapeletas] = useState<PapeletaDeSitio[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCarta, setSelectedCarta] = useState<Carta | null>(null);
    const [selectedPapeleta, setSelectedPapeleta] = useState<PapeletaDeSitio | null>(null);

    useEffect(() => {
        if (!user) {
            setLoading(false);
            return;
        }

        // Fetch cartas in the background — non-blocking
        getCartas()
            .then(result => {
                if (result.success && result.cartas) {
                    setCartas(result.cartas.sort((a, b) =>
                        toDateSafe(b.sentAt).getTime() - toDateSafe(a.sentAt).getTime()
                    ));
                }
            })
            .catch(console.error);

        // Set up papeletas listener immediately — this controls the loading state
        const q = query(collection(db, 'users', user.uid, 'papeletas'), orderBy('sentAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snap) => {
                setPapeletas(snap.docs.map(d => ({
                    ...(sanitizeFirestoreData(d.data()) as PapeletaDeSitio),
                    id: d.id,
                })));
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching papeletas:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user]);

    const handleOpenCarta = async (carta: Carta) => {
        setSelectedCarta(carta);
        if (!carta.isRead && user) {
            await updateDoc(doc(db, 'users', user.uid, 'cartas', carta.id), { isRead: true });
            setCartas(prev => prev.map(c => c.id === carta.id ? { ...c, isRead: true } : c));
        }
    };

    if (loading || authLoading) {
        return (
            <div className="flex h-[calc(100vh-10rem)] items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
        );
    }

    const fullHermano: UserProfile = {
        id: user?.uid || '',
        nickname: userData?.nickname || 'Hermano',
        ...userData,
    };

    return (
        <div className="space-y-8">
            <div>
                <h1 className="font-headline text-4xl font-bold text-primary">Buzón</h1>
                <p className="mt-1 text-muted-foreground text-sm">Tus comunicaciones oficiales y papeletas de sitio.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* ── CARTAS ── */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="rounded-xl bg-primary/10 border border-primary/20 p-2">
                            <Mail className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="font-headline text-xl font-bold">Cartas</h2>
                            <p className="text-xs text-muted-foreground">Comunicaciones de tus hermandades</p>
                        </div>
                        {cartas.filter(c => !c.isRead).length > 0 && (
                            <span className="ml-auto flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground">
                                {cartas.filter(c => !c.isRead).length}
                            </span>
                        )}
                    </div>

                    {cartas.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
                            <Inbox className="h-10 w-10 mb-3 opacity-40" />
                            <p className="text-sm">No tienes cartas.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <AnimatePresence initial={false}>
                                {cartas.map(carta => (
                                    <motion.button
                                        key={carta.id}
                                        layout
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        onClick={() => handleOpenCarta(carta)}
                                        className={cn(
                                            "w-full text-left rounded-2xl border p-4 transition-all duration-200 hover:shadow-md",
                                            !carta.isRead
                                                ? "bg-primary/8 border-primary/25 hover:border-primary/40"
                                                : "bg-card/40 border-white/6 hover:border-white/15 hover:bg-card/60"
                                        )}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={cn(
                                                "rounded-full p-2 flex-shrink-0",
                                                !carta.isRead ? "bg-primary/15" : "bg-muted/60"
                                            )}>
                                                {carta.isRead
                                                    ? <MailOpen className="h-4 w-4 text-muted-foreground" />
                                                    : <Mail className="h-4 w-4 text-primary" />
                                                }
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                                                    <p className={cn("text-sm font-semibold truncate", !carta.isRead ? "text-primary" : "text-foreground")}>
                                                        {carta.emitterName}
                                                    </p>
                                                    <span className="text-[0.65rem] text-muted-foreground flex-shrink-0 flex items-center gap-1">
                                                        <Clock className="h-2.5 w-2.5" />
                                                        {formatDistanceToNow(toDateSafe(carta.sentAt), { locale: es, addSuffix: true })}
                                                    </span>
                                                </div>
                                                <p className={cn("text-sm truncate", !carta.isRead ? "font-medium text-foreground" : "text-muted-foreground")}>
                                                    {carta.subject}
                                                </p>
                                                {carta.votacionId && (
                                                    <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[0.65rem] font-semibold text-primary">
                                                        <Vote className="h-3 w-3" /> Votación
                                                    </span>
                                                )}
                                            </div>
                                            {!carta.isRead && (
                                                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                                            )}
                                        </div>
                                    </motion.button>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>

                {/* ── PAPELETAS ── */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-2">
                            <FileText className="h-5 w-5 text-amber-400" />
                        </div>
                        <div>
                            <h2 className="font-headline text-xl font-bold">Papeletas de Sitio</h2>
                            <p className="text-xs text-muted-foreground">Asignaciones de tu hermandad</p>
                        </div>
                        {papeletas.length > 0 && (
                            <span className="ml-auto flex h-6 min-w-6 items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/30 px-1.5 text-xs font-bold text-amber-400">
                                {papeletas.length}
                            </span>
                        )}
                    </div>

                    {papeletas.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
                            <FileText className="h-10 w-10 mb-3 opacity-40" />
                            <p className="text-sm">No tienes papeletas de sitio.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <AnimatePresence initial={false}>
                                {papeletas.map(pap => (
                                    <motion.button
                                        key={pap.id}
                                        layout
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        onClick={() => setSelectedPapeleta(pap)}
                                        className="text-left rounded-2xl border border-white/8 bg-card/40 hover:bg-card/60 hover:border-white/20 hover:shadow-lg transition-all duration-200 overflow-hidden"
                                    >
                                        <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${pap.ornamentColor || '#9b8854'}, ${pap.selloColor || '#9b8854'})` }} />
                                        <div className="p-4">
                                            <div className="flex items-center gap-3 mb-3">
                                                <Avatar className="h-10 w-10 flex-shrink-0 border border-white/10">
                                                    <AvatarImage src={pap.hermandadEscudo} />
                                                    <AvatarFallback className="text-xs">{pap.hermandadNombre?.charAt(0)}</AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-bold text-sm truncate leading-tight">{pap.hermandadNombre}</p>
                                                    <p className="text-[0.65rem] text-muted-foreground flex items-center gap-1 mt-0.5">
                                                        <Building2 className="h-2.5 w-2.5 flex-shrink-0" />
                                                        <span className="truncate">{pap.hermandadSede}</span>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-muted-foreground">Cargo</span>
                                                    <span className="font-semibold truncate max-w-[55%] text-right">{pap.cargo}</span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-muted-foreground">Día</span>
                                                    <span className="font-semibold truncate max-w-[55%] text-right">{pap.diaDeSalida}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="px-4 pb-3">
                                            <span className="text-[0.65rem] text-muted-foreground flex items-center gap-1">
                                                <Clock className="h-2.5 w-2.5" />
                                                {formatDistanceToNow(toDateSafe(pap.sentAt), { locale: es, addSuffix: true })}
                                            </span>
                                        </div>
                                    </motion.button>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Carta detail dialog ── */}
            <Dialog open={!!selectedCarta} onOpenChange={(open) => !open && setSelectedCarta(null)}>
                <DialogContent className="max-w-2xl bg-card/95 backdrop-blur-xl border-white/10 p-0 overflow-hidden">
                    {selectedCarta && (
                        <>
                            <div className="border-b border-white/8 px-6 py-5">
                                <DialogHeader>
                                    <div className="flex items-center gap-3 mb-1">
                                        <div className="rounded-lg bg-primary/10 p-1.5 flex-shrink-0">
                                            <Mail className="h-4 w-4 text-primary" />
                                        </div>
                                        <p className="text-sm text-muted-foreground font-medium">
                                            De <strong className="text-foreground">{selectedCarta.emitterName}</strong>
                                        </p>
                                        <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                                            {format(toDateSafe(selectedCarta.sentAt), "d MMM yyyy · HH:mm", { locale: es })}
                                        </span>
                                    </div>
                                    <DialogTitle className="text-xl font-bold">{selectedCarta.subject}</DialogTitle>
                                </DialogHeader>
                            </div>
                            <ScrollArea className="max-h-[60vh]">
                                <div className="px-6 py-5 prose prose-sm dark:prose-invert max-w-none">
                                    <ReactMarkdown>{selectedCarta.content}</ReactMarkdown>
                                </div>
                                {selectedCarta.votacionId && selectedCarta.hermandadId && (
                                    <div className="px-6 pb-6">
                                        <VotacionCarta hermandadId={selectedCarta.hermandadId} votacionId={selectedCarta.votacionId} />
                                    </div>
                                )}
                            </ScrollArea>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Papeleta detail dialog ── */}
            <Dialog open={!!selectedPapeleta} onOpenChange={(open) => !open && setSelectedPapeleta(null)}>
                <DialogContent className="max-w-xl bg-card/95 backdrop-blur-xl border-white/10">
                    {selectedPapeleta && (
                        <div className="flex flex-col items-center py-2">
                            {/* Mismo componente que usa la hermandad al emitirla → idénticas. */}
                            <PapeletaDeSitioDoc papeleta={selectedPapeleta} hermano={fullHermano} />
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
